import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Carrot,
  Globe,
  Soup,
  Wrench,
  Flame,
  Search,
  Loader2,
  X,
  BookOpenText,
  Lightbulb,
  RefreshCw,
} from 'lucide-react';
import { useTheme } from '@framework/contexts/ThemeContext';
import BaseModal from '@framework/components/BaseModal';
import ActionButton from '@framework/components/ActionButton';
import ErrorAlert from '@framework/components/ErrorAlert';

const PAGE_SIZE = 24;

const TABS = [
  { key: 'ingredients', label: 'Ingredients', singular: 'ingredient', icon: Carrot },
  { key: 'cuisines',    label: 'Cuisines',    singular: 'cuisine',    icon: Globe },
  { key: 'dishes',      label: 'Dishes',      singular: 'dish',       icon: Soup },
  { key: 'equipment',   label: 'Equipment',   singular: 'equipment',  icon: Wrench },
  { key: 'techniques',  label: 'Techniques',  singular: 'technique',  icon: Flame },
];

// Fields to display in detail modal per entity type
const DETAIL_FIELDS = {
  ingredient: [
    { key: 'description', label: 'Description' },
    { key: 'nutrition_info', label: 'Nutrition Info' },
    { key: 'substitutes', label: 'Substitutes', type: 'list' },
    { key: 'storage_tips', label: 'Storage Tips' },
    { key: 'fun_facts', label: 'Fun Facts', type: 'list' },
  ],
  cuisine: [
    { key: 'description', label: 'Description' },
    { key: 'flavor_profile', label: 'Flavor Profile' },
    { key: 'history', label: 'History' },
    { key: 'signature_dishes', label: 'Signature Dishes', type: 'list' },
    { key: 'key_ingredients', label: 'Key Ingredients', type: 'list' },
    { key: 'cooking_techniques', label: 'Cooking Techniques', type: 'list' },
    { key: 'fun_facts', label: 'Fun Facts', type: 'list' },
  ],
  dish: [
    { key: 'description', label: 'Description' },
    { key: 'origin_cuisine', label: 'Origin Cuisine' },
    { key: 'ingredients', label: 'Ingredients', type: 'list' },
    { key: 'cooking_method', label: 'Cooking Method' },
    { key: 'serving_suggestions', label: 'Serving Suggestions' },
    { key: 'variations', label: 'Variations', type: 'list' },
    { key: 'similar_dishes', label: 'Similar Dishes Around the World', type: 'list' },
    { key: 'fun_facts', label: 'Fun Facts', type: 'list' },
  ],
  equipment: [
    { key: 'description', label: 'Description' },
    { key: 'category', label: 'Category' },
    { key: 'materials', label: 'Materials', type: 'list' },
    { key: 'best_for', label: 'Best For', type: 'list' },
    { key: 'care_tips', label: 'Care Tips' },
    { key: 'buying_tips', label: 'Buying Tips' },
    { key: 'related_equipment', label: 'Related Equipment', type: 'list' },
    { key: 'fun_facts', label: 'Fun Facts', type: 'list' },
  ],
  technique: [
    { key: 'description', label: 'Description' },
    { key: 'category', label: 'Category' },
    { key: 'equipment_needed', label: 'Equipment Needed', type: 'list' },
    { key: 'best_for', label: 'Best For', type: 'list' },
    { key: 'tips', label: 'Tips', type: 'list' },
    { key: 'common_mistakes', label: 'Common Mistakes', type: 'list' },
    { key: 'related_techniques', label: 'Related Techniques', type: 'list' },
    { key: 'fun_facts', label: 'Fun Facts', type: 'list' },
  ],
};

const EncyclopediaPage = () => {
  const { apiService: api } = useTheme();
  const [activeTab, setActiveTab] = useState('ingredients');
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null); // { name, singular }
  const sentinelRef = useRef(null);
  const [loadKey, setLoadKey] = useState(0);

  const searchTimeout = useRef(null);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
      setItems([]);
      setHasMore(true);
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [searchTerm]);

  const loadItems = useCallback(async () => {
    try {
      const isInitial = page === 0;
      if (isInitial) setLoading(true);
      else setLoadingMore(true);
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      const result = await api.getEncyclopedia(activeTab, params);
      const batch = result[activeTab] || [];
      if (isInitial) {
        setItems(batch);
      } else {
        setItems(prev => [...prev, ...batch]);
      }
      setTotalCount(result.total || 0);
      setHasMore(batch.length === PAGE_SIZE);
      setError(null);
    } catch (err) {
      console.error('Error loading encyclopedia:', err);
      setError('Failed to load items');
      if (page === 0) setItems([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [api, activeTab, page, debouncedSearch, loadKey]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPage(0);
    setSearchTerm('');
    setDebouncedSearch('');
    setItems([]);
    setHasMore(true);
  };

  const currentTab = TABS.find(t => t.key === activeTab);

  const handleRefresh = useCallback(() => {
    setItems([]);
    setHasMore(true);
    setPage(0);
    setLoadKey(k => k + 1);
  }, []);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          setPage(p => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpenText className="w-6 h-6 text-orange-500" />
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Encyclopedia</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">{totalCount} cached {currentTab?.label.toLowerCase()}</p>
              </div>
            </div>
            <ActionButton icon={RefreshCw} onClick={handleRefresh} variant="secondary" size="sm">Refresh</ActionButton>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 pb-0">
          <div className="flex flex-wrap gap-2">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${currentTab?.label.toLowerCase()}...`}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 pb-4">
            <ErrorAlert error={error} variant="error" />
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        )}

        {/* Card Grid */}
        {!loading && items.length > 0 && (
          <div className="px-6 pb-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {items.map(item => (
                <ItemCard
                  key={item.id}
                  item={item}
                  tab={currentTab}
                  onClick={() => setSelectedItem({ name: item.name, singular: currentTab.singular })}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && items.length === 0 && !error && (
          <div className="p-12 text-center">
            {currentTab && <currentTab.icon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />}
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No {currentTab?.label.toLowerCase()} found
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {debouncedSearch
                ? 'Try adjusting your search.'
                : 'No AI-generated info has been cached yet for this type.'}
            </p>
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />
        {loadingMore && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <EntityDetailModal
          name={selectedItem.name}
          type={selectedItem.singular}
          api={api}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};


// ============================================================================
// Item Card
// ============================================================================

const ItemCard = ({ item, tab, onClick }) => {
  const Icon = tab.icon;
  const badge = item.category || item.origin_cuisine;

  return (
    <button
      onClick={onClick}
      className="group text-left bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-600 hover:shadow-md transition-all"
    >
      {item.image_url ? (
        <div className="aspect-square overflow-hidden">
          <img
            src={item.image_url}
            alt={item.display_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        </div>
      ) : (
        <div className="aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          <Icon className="w-10 h-10 text-gray-300 dark:text-gray-600" />
        </div>
      )}
      <div className="p-2.5">
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {item.display_name}
        </div>
        {badge && (
          <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">
            {badge}
          </span>
        )}
      </div>
    </button>
  );
};


// ============================================================================
// Entity Detail Modal
// ============================================================================

const EntityDetailModal = ({ name, type, api, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadDetail = async () => {
      try {
        setLoading(true);
        const result = await api.getEntityInfo(type, name);

        if (cancelled) return;

        if (result.status === 'processing' && result.task_id) {
          setPolling(true);
          setLoading(false);
          pollRef.current = setInterval(async () => {
            try {
              const status = await api.getEntityInfoStatus(type, result.task_id);
              if (cancelled) return;
              if (status.status === 'completed') {
                clearInterval(pollRef.current);
                setData(status[type] || status);
                setPolling(false);
              } else if (status.status === 'failed') {
                clearInterval(pollRef.current);
                setError(status.error || 'Generation failed');
                setPolling(false);
              }
            } catch {
              clearInterval(pollRef.current);
              setError('Failed to check status');
              setPolling(false);
            }
          }, 2000);
        } else {
          setData(result[type] || result);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load details');
          setLoading(false);
        }
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [api, type, name]);

  const fields = DETAIL_FIELDS[type] || [];

  return (
    <BaseModal isOpen={true} onClose={onClose} title={data?.display_name || name} size="xl">
      <div className="p-6 space-y-5">
        {/* Loading */}
        {(loading || polling) && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {polling ? 'Generating info...' : 'Loading...'}
            </p>
          </div>
        )}

        {/* Error */}
        {error && <ErrorAlert error={error} variant="error" />}

        {/* Content */}
        {data && !loading && !polling && (
          <>
            {/* Image + title */}
            {data.image_url && (
              <div className="flex justify-center">
                <img
                  src={data.image_url}
                  alt={data.display_name}
                  className="w-full max-w-md rounded-lg object-cover max-h-64"
                />
              </div>
            )}

            {/* Fields */}
            <div className="space-y-4">
              {fields.map(field => {
                const value = data[field.key];
                if (!value || (Array.isArray(value) && value.length === 0)) return null;

                return (
                  <div key={field.key}>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                      {field.key === 'fun_facts' && <Lightbulb className="w-3.5 h-3.5 text-yellow-500" />}
                      {field.label}
                    </h4>
                    {field.type === 'list' ? (
                      <ul className="list-disc list-inside space-y-1">
                        {(Array.isArray(value) ? value : [value]).map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 dark:text-gray-400">{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default EncyclopediaPage;
